import formCollection from "../../server/formCollection";
import dynamicFunctions from "../../config/dynamicFunctions";
import formSubmitterServer from "../../server/formSubmitter";
import { computeExpression } from "../../helpers/dynamicComputer";
import {
  getFormBuilderComponents,
  getElementComponentData
} from "../../helpers/formBuilder";
import serverEForm from "../../server/eForm";
import { formatDateTimeMysql } from "../../helpers/date";

function getSignatureCounter(formValues) {
  var count = 0;
  var completed = 0;
  for (var value in formValues) {
    if (value.startsWith('Signature')) {
      count++;
      if (formValues[value]) completed++;
    }
  }
  return {
    totalSignatures: count,
    completedSignatures: completed
  };
}

const getVariablesCodeForCompute = ({
  formScript,
  values,
  metadata,
  document,
  collections,
  client,
  element,
  form,
  location,
  otherResults,
  showType
}) => {
  let variablesCode = [formScript, "var components = {};"];
  dynamicFunctions.forEach(func => variablesCode.push(func));
  let controlIds = Object.keys(values);
  controlIds = [...new Set(controlIds.concat(Object.keys(metadata)))];
  for (let index = 0; index < controlIds.length; index++) {
    const controlId = controlIds[index];
    variablesCode.push(`
      components['${controlId}']={
        Value: ${JSON.stringify(values[controlId])},
        ${
          metadata[controlId]
            ? Object.keys(metadata[controlId])
                .map(
                  metadataProperty =>
                    `${metadataProperty}: ${JSON.stringify(
                      metadata[controlId][metadataProperty]
                    )}`
                )
                .join(",")
            : ""
        }
      };
    `);
  }
  variablesCode.push(`
    var document = ${JSON.stringify(document)};
  `);

  variablesCode.push(`
    var collections = ${JSON.stringify(collections)};
  `);

  variablesCode.push(`
    var client = ${JSON.stringify(client)};
  `);

  variablesCode.push(`
    var patient = client;
  `);

  variablesCode.push(`
    var form = ${JSON.stringify(form)};
  `);

  variablesCode.push(`
    var element = ${JSON.stringify(element)};
  `);

  variablesCode.push(`
    var location = ${JSON.stringify(location)};
  `);

  variablesCode.push(`
    var otherResults = ${JSON.stringify(otherResults)};
  `);

  variablesCode.push(`
    var showType = ${JSON.stringify(showType)};
  `);
  return variablesCode.join("\n");
};

const initialFormJson = () => ({
  elType: "Form",
  id: "form1",
  properties: {
    script: "",
    scoreFunctionBody: ""
  },
  grid: {
    components: []
  },
  layout: {
    rows: []
  }
});

const hasValue = variable => {
  if (typeof variable == "string") {
    return variable != "";
  }
  if (typeof variable == "number") {
    return variable != 0;
  }
  if (variable instanceof Array) {
    return variable.length != 0;
  }
  return false;
};

const formSubmitterModule = {
  namespaced: true,
  state: () => ({
    form: {
      id: null,
      name: "",
      json: initialFormJson(),
      values: {},
      computedProperties: {},
      metadata: {},
      document: {},
      client: {},
      eoc: {},
      versionId: "",
      collections: {},
      otherResults: [],
      showType: "",
      errors: {},
      hasUnsavedChanges: false,
      oneTimeBindingValues: {}
    },
    clipboard: localStorage.getItem("documentClipboard")
      ? JSON.parse(localStorage.getItem("documentClipboard"))
      : {
          versionId: null,
          values: {}
        }
  }),
  mutations: {
    async setInitialValues(state) {
      state.form = {
        id: null,
        name: "",
        json: initialFormJson(),
        values: {},
        computedProperties: {},
        metadata: {},
        document: {},
        client: {},
        eoc: {},
        versionId: "",
        collections: {},
        errors: {},
        hasUnsavedChanges: false,
        otherResults: [],
        showType: "",
        oneTimeBindingValues: {}
      };
    },
    async initializeFormSubmitterForm(
      state,
      {
        formId,
        formName,
        version,
        client,
        eoc,
        document,
        isEForm,
        eFormToken,
        otherResults,
        defaultValues,
        showType
      }
    ) {
      state.form.client = client;
      state.form.eoc = eoc;
      state.form.id = formId;
      state.form.name = formName;
      state.form.json = version.content;
      state.form.versionId = version.id;
      state.form.values = document
        ? document.results
        : defaultValues
        ? defaultValues
        : {};
      state.form.computedProperties = {};
      state.form.document = document
        ? {
            status: document.status,
            effectiveDate: formatDateTimeMysql(document.effectiveDate)
          }
        : {
            status: "Draft",
            new: true,
            effectiveDate: formatDateTimeMysql(new Date())
          };
      state.form.metadata = {};
      state.form.errors = {};
      state.form.hasUnsavedChanges = false;
      state.form.oneTimeBindingValues = {};
      let serverCollections = {};
      if (isEForm)
        serverCollections = await serverEForm.queryCollections(eFormToken);
      else serverCollections = await formCollection.query();

      const collections = {};
      const jsonString = JSON.stringify(state.form.json);
      for (let index = 0; index < serverCollections.length; index++) {
        const serverCollection = serverCollections[index];
        if (jsonString.indexOf(`collections.${serverCollection}`) > -1) {
          if (isEForm) {
            const collectionResult = await serverEForm.queryCollection(
              eFormToken,
              serverCollection
            );
            collections[serverCollection] = collectionResult.items;
          } else {
            const collectionResult = await formCollection.get(serverCollection);
            collections[serverCollection] = collectionResult.items;
          }
        }
      }
      state.form.collections = collections;
      state.form.otherResults = otherResults ? otherResults : [];
      state.form.showType = showType;
      setTimeout(() => {
        state.form.values = {
          ...state.form.values
        };
      }, 0);
    },
    async fetchCollections(state, { json }) {
      let serverCollections = {};
      serverCollections = await formCollection.query();

      const collections = {};
      const jsonString = JSON.stringify(json);
      for (let index = 0; index < serverCollections.length; index++) {
        const serverCollection = serverCollections[index];
        if (jsonString.indexOf(`collections.${serverCollection}`) > -1) {
          const collectionResult = await formCollection.get(serverCollection);
          collections[serverCollection] = collectionResult.items;
        }
      }
      state.form.collections = {
        ...state.form.collections,
        ...collections
      };
    },
    setDocumentData(state, { status, effectiveDate }) {
      state.form = {
        ...state.form,
        document: {
          status: status,
          effectiveDate: effectiveDate
        }
      };
    },
    setFormSubmitterComponentValue(
      state,
      { controlId, controlValue, hardSet = false, userAction = false }
    ) {
      if (
        (controlValue || state.form.values[controlId]) &&
        controlValue != state.form.values[controlId] &&
        (!Array.isArray(controlValue) || controlValue.length > 0) &&
        userAction
      ) {
        state.form.hasUnsavedChanges = true;
      }
      if (hardSet) {
        state.form.values = {
          ...state.form.values,
          [controlId]: controlValue
        };
      } else {
        state.form.values[controlId] = controlValue;
      }
    },
    setFormSubmitterComponentMetadata(state, { controlId, controlMetadata }) {
      state.form.metadata = {
        ...state.form.metadata,
        [controlId]: controlMetadata
      };
    },
    setFormSubmitterFormErrors(state, { errors }) {
      state.form.errors = errors;
    },
    setFormSubmitterFormHasUnsavedChanges(state, { hasUnsavedChanges }) {
      state.form.hasUnsavedChanges = hasUnsavedChanges;
    },
    copyFormSubmitterValues(state) {
      const values = { ...state.form.values };
      Object.keys(values).forEach(key => {
        if (
          key.startsWith("Signature") ||
          key.startsWith("ClientAllergies") ||
          key.startsWith("ClientDiagnosisCodes") ||
          key.startsWith("ClientVitals") ||
          key.startsWith("TreatmentPlanObjectives")
        ) {
          delete values[key];
        }
      });
      state.clipboard.values = values;
      state.clipboard.versionId = state.form.versionId;
      localStorage.setItem(
        "documentClipboard",
        JSON.stringify(state.clipboard)
      );
    },
    pasteFormSubmitterValues(state) {
      state.form.values = { ...state.form.values, ...state.clipboard.values };
      state.form.hasUnsavedChanges = true;
    },
    setOneTimeBindingValues(state, { oneTimeBindingValues }) {
      state.form.oneTimeBindingValues = oneTimeBindingValues;
    }
  },
  actions: {
    validateFormSubmitterForm(context) {
      let errors = {};
      const components = getFormBuilderComponents(context.state.form.json);
      for (let index = 0; index < components.length; index++) {
        const component = components[index];
        const componentData = getElementComponentData(
          component.elType,
          component.version
        );
        const validationErrors = componentData.validate({
          value: context.state.form.values[component.id],
          properties: component.properties
        });
        if (validationErrors.length > 0)
          errors[component.id] = validationErrors;
      }
      context.commit("setFormSubmitterFormErrors", { errors });
      return Object.keys(errors).length == 0;
    },
    copyFormValuesToClipboard(context) {
      context.commit("copyFormSubmitterValues");
      context.dispatch(
        "layout/showAlertSuccess",
        {
          message: "Copied to clipboard"
        },
        {
          root: true
        }
      );
    },
    pasteFormValuesFromClipboard(context) {
      if (context.state.clipboard.versionId == context.state.form.versionId) {
        context.commit("pasteFormSubmitterValues");
        context.dispatch(
          "layout/showAlertSuccess",
          {
            message: "Content pasted"
          },
          {
            root: true
          }
        );
      } else {
        context.dispatch(
          "layout/showError",
          {
            message:
              "The format of the copied document doesn't match the current one. You can only copy between documents that are the same type and version."
          },
          {
            root: true
          }
        );
      }
    },
    async addFormSubmitterFile(context, data) {
      const response = await formSubmitterServer.upload(data);
      return response.id;
    },
    async updateFormSubmitterFile(context, data) {
      await formSubmitterServer.uploadUpdate(data);
    },
    async addFormSubmitterForm(
      context,
      {
        sharedDocumentId,
        documentGroupId,
        status,
        groupSessionId,
        effectiveDate,
        nextReviewDate,
        parentDocumentId,
        bakedLayout,
        scheduleEventId,
        scheduleEventParticipantId,
        notes,
        score,
        attachForm
      }
    ) {
      let submitId = "";
      const {totalSignatures, completedSignatures} = getSignatureCounter(context.state.form.values);
      const result = await formSubmitterServer.create({
        results: context.state.form.values,
        formId: context.state.form.id,
        patientId: context.state.form.client.id,
        episodeOfCareId: context.state.form.eoc
          ? context.state.form.eoc.id
          : null,
        versionId: context.state.form.versionId,
        status,
        mode: "Results",
        documentGroupId,
        groupSessionId,
        bakedLayout,
        score: score != null ? score.toString() : score,
        effectiveDate,
        nextReviewDate,
        parentDocumentId,
        scheduleEventId,
        scheduleEventParticipantId,
        notes,
        totalSignatures,
        completedSignatures,
        attachForm: attachForm
        
      }, sharedDocumentId);
      submitId = result.id;
      const documentRefNumber = result.documentRefNumber;
      return { submitId, documentRefNumber, totalSignatures, completedSignatures };
    },
    async editFormSubmitterForm(
      context,
      {
        submitId,
        documentGroupId,
        groupSessionId,
        status,
        effectiveDate,
        nextReviewDate,
        parentDocumentId,
        bakedLayout,
        scheduleEventId,
        scheduleEventParticipantId,
        notes,
        score,
        unlockReason
      }
    ) {
      const {totalSignatures, completedSignatures} = getSignatureCounter(context.state.form.values);
      await formSubmitterServer.update(submitId, {
        results: context.state.form.values,
        formId: context.state.form.id,
        patientId: context.state.form.client.id,
        episodeOfCareId: context.state.form.eoc.id,
        versionId: context.state.form.versionId,
        status,
        mode: "Results",
        documentGroupId,
        groupSessionId,
        bakedLayout,
        score: score != null ? score.toString() : score,
        effectiveDate,
        nextReviewDate,
        parentDocumentId,
        scheduleEventId,
        scheduleEventParticipantId,
        notes,
        unlockReason,
        totalSignatures,
        completedSignatures
      });
      return {totalSignatures, completedSignatures}
    },
    async bulkEditFormSubmitterForm(context, data) {
      return await formSubmitterServer.bulkUpdate(data);
    },
    async deleteFormSubmitterSubmit(context, { submitId }) {
      return await formSubmitterServer.delete(submitId);
    }
  },
  getters: {
    getFormSubmitterComponentValue: state => controlId => {
      return state.form.values[controlId];
    },
    computeFormSubmitterValue: (state, getters, rootState) => ({
      elementId,
      property,
      value,
      defaultValue,
      form,
      statements,
      preCode
    }) => {
      let result = value;

      let formData = state.form;
      if (form) {
        try {
          formData = {
            json: form.version ? form.version.content : form.current.content,
            values: form.submit ? form.submit.results : {},
            metadata: state.form.metadata,
            document: form.submit,
            collections: state.form.collections,
            client: form.client,
            name: form.name,
            otherResults: form.otherResults ? form.otherResults : [],
            showType: form.showType ? form.showType : state.form.showType
          };
        } catch (error) {
          formData = form;
        }
      }
      if (typeof value == "string" && value.startsWith("computed:")) {
        let expression = getVariablesCodeForCompute({
          formScript: formData.json.properties.script,
          values: formData.values,
          metadata: formData.metadata,
          document: formData.document,
          collections: formData.collections,
          client: formData.client,
          element: {
            id: elementId
          },
          form: {
            name: formData.name
          },
          location: rootState.layout.selectedLocation,
          otherResults: formData.otherResults,
          showType: formData.showType
        });
        if (preCode) expression += " \n" + preCode + " \n";
        if (statements) expression += " \n" + `${value.slice(9)};`;
        else {
          expression +=
            " \n" +
            `var _result = ${value.slice(9)}; \n JSON.stringify(_result)`;
        }

        const computeResult = computeExpression(
          expression,
          statements ? true : false,
          {
            setComponentValue: (conponentId, componentValue) => {
              state.form.values = {
                ...state.form.values,
                [conponentId]: componentValue
              };
            }
          }
        );
        if (computeResult == null) result = defaultValue;
        else result = computeResult;
      }
      if (elementId && property) {
        if (!state.form.computedProperties[elementId]) {
          state.form.computedProperties[elementId] = {};
        }
        state.form.computedProperties[elementId][property] = result;
      }

      return result;
    },
    superComputeFormSubmitterValue: (state, getters) => ({
      elementId,
      property,
      value,
      defaultValue,
      form,
      statements,
      preCode
    }) => {
      let result = value;
      if (typeof value == "string") {
        if (statements) result = result.replaceAll("\n", ";");
        let re = /\{\{(?:(?!\}\}).)*\}\}/g;
        const matches = [...result.matchAll(re)];
        if (
          matches.length == 1 &&
          value.startsWith("{{") &&
          value.endsWith("}}")
        ) {
          let oneTimeBindingKey = `${elementId}${
            property ? "_" + property : ""
          }`;
          if (oneTimeBindingKey in state.form.oneTimeBindingValues) {
            result = state.form.oneTimeBindingValues[oneTimeBindingKey];
          } else {
            let isOneTimeBinding = false;
            let toComputeValue = value.slice(2, value.length - 2);
            if (toComputeValue.startsWith("::")) {
              isOneTimeBinding = true;
              toComputeValue = toComputeValue.slice(2, value.length);
            }
            result = getters.computeFormSubmitterValue({
              elementId,
              property,
              value: "computed:" + toComputeValue,
              defaultValue,
              form,
              statements,
              preCode
            });
            if (isOneTimeBinding && hasValue(result)) {
              state.form.oneTimeBindingValues[oneTimeBindingKey] = result;
            }
          }
        } else {
          for (let index = matches.length - 1; index >= 0; index--) {
            const match = matches[index];
            const matchStr = match[0];
            const matchIndex = match["index"];

            let computedValue = "";

            let oneTimeBindingKey = `${elementId}${
              property ? "_" + property : ""
            }_${matchIndex}`;
            if (oneTimeBindingKey in state.form.oneTimeBindingValues) {
              computedValue =
                state.form.oneTimeBindingValues[oneTimeBindingKey];
            } else {
              let isOneTimeBinding = false;
              let toComputeValue = matchStr.slice(2, matchStr.length - 2);
              if (toComputeValue.startsWith("::")) {
                isOneTimeBinding = true;
                toComputeValue = toComputeValue.slice(2, toComputeValue.length);
              }
              computedValue = getters.computeFormSubmitterValue({
                elementId,
                property,
                value: "computed:" + toComputeValue,
                defaultValue,
                form,
                statements,
                preCode
              });

              if (isOneTimeBinding && hasValue(result)) {
                state.form.oneTimeBindingValues[
                  oneTimeBindingKey
                ] = computedValue;
              }
            }
            result =
              result.substring(0, matchIndex) +
              computedValue +
              result.substring(matchIndex + matchStr.length);
          }
        }
        getters.computeFormSubmitterValue({
          elementId,
          property: "value",
          value: result,
          defaultValue: "",
          form,
          preCode
        });
      }
      return result;
    },
    getFormSubmitById: () => async id => {
      return await formSubmitterServer.get(id);
    }
  }
};

export default formSubmitterModule;
